import { TransactionsLists } from 'vopay';
import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchRTRWithdrawTransaction from '@/hooks/useApi/useFetchRTRWithdrawTransaction';
import VopayInstantWithdrawalInfo from './VopayInstantWithdrawalInfo';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type TransactionProps = {
  TransactionID: string;
  notes: string;
  isParent?: boolean;
  jwt: string;
  eLinxRequestID?: string;
  // RTRWithdrawTransactionResponse has no parent-linking field of its own, so
  // TransactionDetails.tsx resolves the parent generically by ID and passes it down — shared with
  // FundingStatusBanner instead of this container fetching its own copy.
  parentTransaction?: TransactionsLists;
} & Omit<
  ComponentProps<typeof VopayInstantWithdrawalInfo>,
  | 'transaction'
  | 'Notes'
  | 'isParent'
  | 'ELinxRequestID'
  | 'parentTransactionId'
  | 'parentTransactionStatus'
  | 'parentType'
>;

export default function VopayInstantWithdrawalContainer({
  TransactionID,
  notes,
  isParent = false,
  jwt,
  eLinxRequestID = '',
  parentTransaction,
  ...props
}: TransactionProps) {
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { rtrWithdrawTransaction, fetchRTRWithdrawTransaction } = useFetchRTRWithdrawTransaction({
    bearer: jwt,
  });

  useEffect(() => {
    blockingLoader.show();

    fetchRTRWithdrawTransaction(TransactionID)
      .catch((error) => {
        // Not returned: the dialog's promise settles on dismissal, and the loader must drop before that.
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred'));
      })
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = rtrWithdrawTransaction;

  return (
    <div>
      {transaction && (
        <VopayInstantWithdrawalInfo
          transaction={transaction}
          Notes={notes}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={eLinxRequestID}
          parentTransactionId={parentTransaction?.TransactionID}
          parentTransactionStatus={parentTransaction?.TransactionStatus}
          parentType={parentTransaction?.TransactionType}
          {...props}
        />
      )}
    </div>
  );
}
